const VIDYARD_SDK_SRC = 'https://play.vidyard.com/embed/v4.js';
const VIDYARD_BUTTON_SELECTOR = '[id^="player-button-"]';
const VIDYARD_EMBED_SELECTOR = '.vidyard-player-embed';

function hasVidyardApi() {
  return Boolean(window.vidyardEmbed && window.vidyardEmbed.api);
}

function waitForVidyardApi(timeoutMs = 8000) {
  if (hasVidyardApi()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    function check() {
      if (hasVidyardApi()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Vidyard API was not available after script load'));
        return;
      }

      requestAnimationFrame(check);
    }

    check();
  });
}

function loadVidyardScript() {
  if (hasVidyardApi()) {
    return Promise.resolve();
  }

  if (window.__mheVidyardLoaderPromise) {
    return window.__mheVidyardLoaderPromise;
  }

  window.__mheVidyardLoaderPromise = new Promise((resolve, reject) => {
    let tag = document.querySelector(`script[src="${VIDYARD_SDK_SRC}"]`);
    const scriptAlreadyPresent = Boolean(tag);

    if (!tag) {
      tag = document.createElement('script');
      tag.src = VIDYARD_SDK_SRC;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }
    }

    const handleLoad = () => {
      waitForVidyardApi()
        .then(() => {
          window.hasAlreadyLoadedVidyard = true;
          resolve();
        })
        .catch(reject);
    };

    const handleError = () => {
      window.__mheVidyardLoaderPromise = null;
      window.hasAlreadyLoadedVidyard = false;
      reject(new Error('Failed to load Vidyard script'));
    };

    tag.addEventListener('load', handleLoad, { once: true });
    tag.addEventListener('error', handleError, { once: true });

    if (scriptAlreadyPresent) {
      waitForVidyardApi()
        .then(resolve)
        .catch(() => {
          // Keep waiting for native load/error events when script exists but API is not ready yet.
        });
    }
  });

  return window.__mheVidyardLoaderPromise;
}

function observeVidyardThumbnails() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const thumbnails = document.querySelectorAll('.vidyard-lightbox-image');
        if (thumbnails.length === 0) {
          return;
        }

        thumbnails.forEach((thumbnail) => {
          thumbnail.alt = '';
        });

        observer.disconnect();
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function setMargin(navbar, close, video) {
  const mobileHeader = document.getElementById('mobileHeader');
  if (!mobileHeader || mobileHeader.offsetWidth !== 0) {
    return;
  }

  const headerRect = navbar.getBoundingClientRect();
  const y = headerRect.height + headerRect.y;
  close.style.marginTop = `${y}px`;
  video.style.marginTop = `${y}px`;
}

function checkMediaQuery(header, navbar, close, video, content, overlay) {
  if (window.innerWidth < 768) {
    header.style.zIndex = '100';
    close.style.marginTop = '0px';
    video.style.marginTop = '0px';
    setTimeout(() => {
      let contentElement = content;
      let overlayElement = overlay;
      if (!contentElement || !overlayElement) {
        contentElement = document.getElementById('vidyard-content-fixed');
        overlayElement = document.getElementById('vidyard-overlay');
      }

      if (!contentElement || !overlayElement) {
        return;
      }

      close.style.zIndex = '1500';
      overlayElement.style.zIndex = '1500';
      contentElement.style.zIndex = '1500';
    }, 0);
    return;
  }

  header.style.zIndex = null;
  close.style.zIndex = null;
  if (content) {
    content.style.zIndex = null;
  }
  if (overlay) {
    overlay.style.zIndex = null;
  }
  setMargin(navbar, close, video);
}

function addVidyardLayoutListeners() {
  if (window.__mheVidyardLayoutListenersAttached) {
    return;
  }

  const navbar = document.getElementById('navbar');
  const header = document.getElementById('mheHeader');
  const close = document.getElementsByClassName('vidyard-close-container')[0];
  const video = document.getElementById('vidyard-popbox');
  const content = document.getElementById('vidyard-content-fixed');
  const overlay = document.getElementById('vidyard-overlay');

  if (!navbar || !header || !close || !video) {
    return;
  }

  setMargin(navbar, close, video);
  checkMediaQuery(header, navbar, close, video, content, overlay);

  window.addEventListener('scroll', () => {
    setMargin(navbar, close, video);
  });

  window.addEventListener('resize', () => {
    checkMediaQuery(header, navbar, close, video, content, overlay);
  });

  window.__mheVidyardLayoutListenersAttached = true;
}

function trackAndShowPlayer(button, player) {
  window.alreadyTrackedVidyardLightboxPlayers = window.alreadyTrackedVidyardLightboxPlayers || [];
  if (!window.alreadyTrackedVidyardLightboxPlayers.includes(player)) {
    window.alreadyTrackedVidyardLightboxPlayers.push(player);
  }
  button.associatedplayer = player;
  player.showLightbox();
  addVidyardLayoutListeners();
}

function bindVidyardButtons() {
  const buttons = document.querySelectorAll(VIDYARD_BUTTON_SELECTOR);
  buttons.forEach((button) => {
    if (button.dataset.vidyardBound === 'true') {
      return;
    }

    const source = (button.dataset.layerVideoSource || '').trim().toLowerCase();
    if (source && source !== 'vidyard') {
      return;
    }

    const idSuffix = button.id.replace('player-button-', '');
    const container = document.getElementById(`player-container-${idSuffix}`);
    const uuid = button.dataset.layerVideoId || button.dataset.uuid || container?.dataset.layerVideoId;

    if (!container || !uuid) {
      return;
    }

    button.addEventListener('click', async () => {
      if (button.dataset.vidyardPending === 'true') {
        return;
      }

      button.dataset.vidyardPending = 'true';

      try {
        await loadVidyardScript();
      } catch (error) {
        // Preserve existing behavior: log and stop when SDK can't load.
        console.error(error);
        button.dataset.vidyardPending = 'false';
        return;
      }

      if (button.associatedplayer) {
        trackAndShowPlayer(button, button.associatedplayer);
        button.dataset.vidyardPending = 'false';
        return;
      }

      if (!hasVidyardApi()) {
        console.error(new Error('Vidyard API unavailable after SDK load'));
        button.dataset.vidyardPending = 'false';
        return;
      }

      window.vidyardEmbed.api
        .renderPlayer({
          uuid,
          container,
          type: 'lightbox',
          vydata: encodeURIComponent(JSON.stringify({ location: 'here', more: 'yes' })),
        })
        .then((player) => {
          trackAndShowPlayer(button, player);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          button.dataset.vidyardPending = 'false';
        });
    });

    button.dataset.vidyardBound = 'true';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector(VIDYARD_BUTTON_SELECTOR) || document.querySelector(VIDYARD_EMBED_SELECTOR)) {
    loadVidyardScript()
      .then(() => {
        observeVidyardThumbnails();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  bindVidyardButtons();

  const observer = new MutationObserver(() => {
    bindVidyardButtons();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
